import { cn } from "@/lib/utils";
import { AdminTreeNode } from "@/types/analysis";

export interface DetailAdminTreeProps {
  nodes: AdminTreeNode[];
  depth?: number;
  parentPath?: string;
}

const hasNoChildren = (nodes: AdminTreeNode[]) =>
  nodes.every((n) => n.children.length === 0);

export const DetailAdminTree = ({
  nodes,
  depth = 0,
  parentPath = ""
}: DetailAdminTreeProps) => {
  const sorted = [...nodes].sort((a, b) => a.name.localeCompare(b.name));

  // If all nodes at this level have no children, render in a comma-separated list
  if (hasNoChildren(sorted)) {
    const names = sorted.map((n) => n.name).join(", ");
    return (
      <div className="flex items-center">
        <div className="relative w-4 h-5 flex-shrink-0">
          <div className="absolute left-0 top-0 h-[10px] border-l border-neutral-300" />
          <div className="absolute left-0 top-[10px] w-[9px] border-t border-neutral-300" />
        </div>
        <span className="text-xs text-txtBody line-clamp-2" title={names}>
          {names}
        </span>
      </div>
    );
  }

  return sorted.map((node, index) => {
    const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
    const isLast = index === sorted.length - 1;
    return (
      <div key={nodePath} className="relative">
        {/* Vertical line */}
        {!isLast && (
          <div className="absolute left-0 top-0 bottom-0 border-l border-neutral-300" />
        )}
        {/* Horizontal connector + node name */}
        <div className="flex items-center">
          <div className="relative w-4 h-5 flex-shrink-0">
            <div className="absolute left-0 top-0 h-[10px] border-l border-neutral-300" />
            <div className="absolute left-0 top-[10px] w-[9px] border-t border-neutral-300" />
          </div>
          <span
            className={cn(
              "text-xs text-txtBody truncate",
              depth === 0 && "text-txtHeading"
            )}
          >
            {node.name}
          </span>
        </div>
        {/* Children */}
        {node.children.length > 0 && (
          <div className="pl-4 pt-1">
            <DetailAdminTree
              nodes={node.children}
              depth={depth + 1}
              parentPath={nodePath}
            />
          </div>
        )}
      </div>
    );
  });
};
